import React, { useState } from 'react';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import api from '../../../../middleware/api';
import { toast } from 'react-toastify';
import { ArrowLeftIcon, CheckCircleIcon, EnvelopeIcon } from '@heroicons/react/24/outline';
import { useNavigate } from 'react-router-dom';

const ForgotPassword = () => {
    const [emailSent, setEmailSent] = useState(false);
    const navigate = useNavigate();

    // Validation schema using Yup
    const validationSchema = Yup.object({
        email: Yup.string().email('Adresse email invalide').required('Requis'),
    });

    const handleGoToLogin = () => {
        navigate('/se-connecter');
    }

    const handleForgotPassword = async (values, { setSubmitting }) => {
        try {
            // Sending forgot password request with email
            const email = values.email.toLowerCase().trim();
            const response = await api.post('/auth/send-reset-password', { email });
            if (response.status === 200) {
                setEmailSent(true);
                toast.success(response.data.message);
            } else {
                toast.error('Échec de l\'envoi de l\'email de réinitialisation. Veuillez réessayer.');
                setEmailSent(false)
            }
        } catch (error) {
            toast.error('Une erreur est survenue. Veuillez réessayer plus tard.');
            setEmailSent(false)
        } finally {
            setSubmitting(false);
        }
    };

    if (emailSent) {
        return (
            <div className="flex flex-col justify-center items-center w-3/4 max-w-md mx-auto p-8 bg-white rounded-lg shadow-lg relative">
                <button
                    onClick={handleGoToLogin}
                    className="absolute top-4 left-4 flex items-center text-gray-600 hover:text-primary transition-colors"
                >
                    <ArrowLeftIcon className="h-5 w-5 mr-1" />
                    <span className="text-sm">Retour</span>
                </button>

                <div className="flex flex-col items-center pt-8">
                    <div className="bg-green-100 p-3 rounded-full mb-6">
                        <CheckCircleIcon className="text-green-500 h-12 w-12" />
                    </div>

                    <h2 className="text-2xl font-bold text-gray-800 mb-6 text-center">Email envoyé !</h2>

                    <div className="text-center mb-6">
                        <p className="text-gray-600 mb-4">
                            Un lien de réinitialisation a été envoyé à votre adresse email.
                        </p>
                        <p className="text-gray-600">
                            Veuillez consulter votre boîte de réception et suivre les instructions.
                        </p>
                    </div>

                    <button
                        onClick={handleGoToLogin}
                        className="mt-4 bg-primary hover:bg-primary-dark text-white font-medium py-3 px-6 rounded-md transition duration-300 shadow-sm"
                    >
                        Retour à la connexion
                    </button>
                </div>
            </div>
        )
    } else {
        return (
            <div className="flex flex-col justify-center items-center w-3/4 max-w-md mx-auto p-8 bg-white rounded-lg shadow-lg relative">
                <button
                    onClick={handleGoToLogin}
                    className="absolute top-4 left-4 flex items-center text-gray-600 hover:text-primary transition-colors"
                >
                    <ArrowLeftIcon className="h-5 w-5 mr-1" />
                    <span className="text-sm">Retour</span>
                </button>

                <h2 className="text-2xl font-bold text-gray-800 mb-8 relative pt-8">
                    Mot de passe oublié ?
                    <span className="absolute bottom-0 left-1/2 transform -translate-x-1/2 w-16 h-1 bg-primary -mb-3"></span>
                </h2>

                <p className="text-gray-600 mb-6 text-center">
                    Entrez l'adresse email associée à votre compte et nous vous enverrons un lien pour réinitialiser votre mot de passe.
                </p>

                <Formik
                    initialValues={{ email: '' }}
                    validationSchema={validationSchema}
                    onSubmit={handleForgotPassword}
                >
                    {({ isSubmitting }) => (
                        <Form className="w-full flex flex-col items-center space-y-6">
                            <div className="w-full">
                                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                                <div className="relative">
                                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                        <EnvelopeIcon className="h-5 w-5 text-gray-400" />
                                    </div>
                                    <Field
                                        type="email"
                                        name="email"
                                        className="w-full bg-gray-50 text-gray-700 text-base font-normal pl-10 pr-3 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition"
                                        autoComplete="email"
                                        placeholder="exemple@domaine.com"
                                    />
                                </div>
                                <ErrorMessage name="email" component="div" className="text-red-500 text-sm mt-1" />
                            </div>

                            <div className="w-full pt-2">
                                <button
                                    type="submit"
                                    className="w-full flex justify-center py-3 px-4 bg-primary hover:bg-primary-dark text-white font-medium rounded-md shadow-sm transition duration-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
                                    disabled={isSubmitting}
                                >
                                    {isSubmitting ? 'Envoi en cours...' : 'Envoyer le lien de réinitialisation'}
                                </button>
                            </div>
                        </Form>
                    )}
                </Formik>
            </div>
        );
    }
};

export default ForgotPassword;
